import React, { useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, IdCell, Input, Modal, PageHead,
  SearchBox, Switch, Textarea, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { categoryApi } from '../api';
import { mediaUrl, CATALOG_SWATCHES } from '../api/media';
import { useToast } from '../context/ToastContext';

const emptyForm = {
  name: '', description: '', image: '', defaultGstRate: 18,
  sortOrder: 0, isFeatured: false, isActive: true, parent: '',
};

const Categories = () => {
  const toast = useToast();
  const list = useList(categoryApi.list, { search: '' });

  const [form, setForm] = useState(null);
  const [remove, setRemove] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  /**
   * Uploads artwork for a category the app will show in place of its icon.
   * The category must exist first — the endpoint is addressed by id — so a
   * brand-new one is saved before the picture can be attached.
   */
  const pickImage = async file => {
    if (!file) return;
    if (!form._id) {
      toast.error('Create the category first, then reopen it to add a picture');
      return;
    }

    setUploading(true);
    try {
      const body = new FormData();
      body.append('image', file);
      const res = await categoryApi.uploadImage(form._id, body);
      setForm(f => ({ ...f, image: res.data.image || res.data.url || f.image }));
      toast.success('Category image updated');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Give the category a name');

    setBusy(true);
    try {
      const payload = { ...form, parent: form.parent || null };
      if (form._id) await categoryApi.update(form._id, payload);
      else await categoryApi.create(payload);
      toast.success(form._id ? 'Category updated' : 'Category created');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRemove = async () => {
    setBusy(true);
    try {
      await categoryApi.remove(remove._id);
      toast.success('Category deleted');
      setRemove(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Category',
      render: row => <IdCell image={mediaUrl(row.image)} name={row.name} sub={`/${row.slug}`} />,
    },
    { key: 'parent', label: 'Parent', render: row => row.parent?.name || <span className="text-muted">Top level</span> },
    { key: 'description', label: 'Description', render: row => <span className="text-sm text-muted">{row.description || '—'}</span> },
    { key: 'defaultGstRate', label: 'GST', align: 'center', render: row => `${row.defaultGstRate}%` },
    { key: 'sortOrder', label: 'Order', align: 'center' },
    {
      key: 'flags',
      label: 'Status',
      render: row => (
        <div className="row" style={{ gap: 5 }}>
          {row.isFeatured && <Badge status="featured" tone="brand" dot={false}>Featured</Badge>}
          <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Live' : 'Hidden'}</Badge>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row, parent: row.parent?._id || '' })}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => setRemove(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Categories"
        subtitle="The tarpaulin taxonomy buyers browse — HDPE, PVC, canvas, covers and liners."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New category</Button>}
      />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search categories…" />
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} categories` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit category' : 'New category'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Create category'}</Button>
          </>
        }
      >
        {form && (
          <>
            <Input label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="HDPE Tarpaulin" />
            <Textarea label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            {/* The app shows this picture instead of a generic icon, so it is
                worth seeing exactly what buyers will see before saving. */}
            <div className="field">
              <label>Category picture</label>
              <div className="cat-image-row">
                <div className="cat-image-preview">
                  {form.image
                    ? <img src={mediaUrl(form.image)} alt={form.name || 'Category'} />
                    : <span>No picture</span>}
                </div>

                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <label className="btn btn-soft btn-sm" style={{ cursor: 'pointer' }}>
                      {uploading ? 'Uploading…' : 'Upload picture'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={uploading}
                        onChange={e => pickImage(e.target.files && e.target.files[0])}
                      />
                    </label>
                    {form.image && (
                      <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, image: '' })}>
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="hint mt-1">Or pick one of the built-in tarpaulin swatches:</div>
                  <div className="swatch-row">
                    {CATALOG_SWATCHES.map(sw => (
                      <button
                        type="button"
                        key={sw.path}
                        title={sw.label}
                        className={`swatch ${form.image === sw.path ? 'on' : ''}`}
                        onClick={() => setForm({ ...form, image: sw.path })}
                      >
                        <img src={mediaUrl(sw.path)} alt={sw.label} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Input label="Image URL" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="/uploads/catalog/… or https://…" hint="Set automatically by the controls above; edit only to point at an external image." />
            <div className="grid-2">
              <Input label="Default GST rate (%)" type="number" value={form.defaultGstRate} onChange={e => setForm({ ...form, defaultGstRate: Number(e.target.value) })} />
              <Input label="Sort order" type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} />
            </div>
            <select className="select mb-2" value={form.parent} onChange={e => setForm({ ...form, parent: e.target.value })}>
              <option value="">No parent (top level)</option>
              {list.rows.filter(c => c._id !== form._id).map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <div className="col" style={{ gap: 11 }}>
              <Switch checked={form.isFeatured} onChange={v => setForm({ ...form, isFeatured: v })} label="Feature on the app home screen" />
              <Switch checked={form.isActive} onChange={v => setForm({ ...form, isActive: v })} label="Visible to buyers" />
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!remove}
        danger
        title="Delete this category?"
        message={`"${remove?.name}" will be removed. This is blocked if any products or sub-categories still reference it.`}
        confirmLabel="Delete"
        loading={busy}
        onConfirm={doRemove}
        onClose={() => setRemove(null)}
      />
    </>
  );
};

export default Categories;
